export type IntakeKnownKind = "in-house" | "outreach" | "volunteer" | "mail" | "prescreen" | "pse-online" | "biennial" | "contingency"

export type IntakeSelectionDraft =
  | { kind: "unset" }
  | { kind: "legacy"; intakeType: string; intakeLocation: string }
  | { kind: IntakeKnownKind; subtypeId: string; code: string; label: string; version: number }

export type IntakeCatalogEntry = {
  id: string
  parentDiscriminator: IntakeKnownKind
  parentLabel: string
  code: string
  longLabel: string
  isEnabled: boolean
  version: number
}

export type IntakeCatalogParent = { discriminator: IntakeKnownKind; label: string }

export function subtypesForParent(entries: IntakeCatalogEntry[], parent: IntakeCatalogParent | undefined) {
  return entries.filter(entry => entry.parentDiscriminator === parent?.discriminator && entry.isEnabled)
}

/** A parent change must never carry a subtype selected for the previous parent. */
export function changeIntakeParent<T extends { intakeType: string; intakeLocation: string }>(draft: T, parentLabel: string) {
  return {
    ...draft,
    intakeType: parentLabel,
    intakeLocation: "",
    ...("intakeSelection" in draft ? { intakeSelection: parentLabel ? null : { kind: "unset" as const } } : {}),
  }
}

/** New selections use the catalog id as the Select value; labels are display text only. */
export function typedSelectionForId(entries: IntakeCatalogEntry[], parent: IntakeCatalogParent | undefined, id: string): IntakeSelectionDraft | null {
  const entry = subtypesForParent(entries, parent).find(candidate => candidate.id === id)
  return entry ? { kind: entry.parentDiscriminator, subtypeId: entry.id, code: entry.code, label: entry.longLabel, version: entry.version } : null
}

/** Compatibility helper for callers that still have only the displayed legacy value. */
export function typedSelectionForLocation(entries: IntakeCatalogEntry[], parent: IntakeCatalogParent | undefined, location: string) {
  const matches = subtypesForParent(entries, parent).filter(candidate => candidate.longLabel === location)
  const match = matches.length === 1 ? matches[0] : undefined
  return match
    ? { kind: match.parentDiscriminator, subtypeId: match.id, code: match.code, label: match.longLabel, version: match.version }
    : null
}
